import React, { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import block from 'bem-cn';

import SVG from 'components/SVG';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import { getActionItems, userTypes } from '../../../data';
import { canPerformFinancialOperation } from '../../../helpers/canPerformFinancialOperation';

import iconSVG from './img/icon.svg';

import './TableRow.scss';

const b = block('adm-bets-table-row-user');

const TableRow = ({ item, isWithdrawalAccess, isDepositAccess, locale }) => {
  const { deposit_level_settings } = useSelector(state => state.userSettings, shallowEqual);
  const authId = useSelector(state => state.auth.id, shallowEqual);

  const canOperate = useMemo(() => canPerformFinancialOperation({ authId, item }), [authId, item]);

  const icon = useMemo(() => {
    if (item.totalDeposits) {
      if (
        item.totalDeposits >= deposit_level_settings.black.from &&
        item.totalDeposits <= deposit_level_settings.black.to
      )
        return (
          <div className={b('td-data-icon-wrapper')}>
            <SVG svgProps={{ svg: iconSVG }} className={b('td-data-icon', { color: 'black' })} />
            <div className={b('td-data-icon-text')}>{locale.black}</div>
          </div>
        );

      if (
        item.totalDeposits >= deposit_level_settings.gold.from &&
        item.totalDeposits <= deposit_level_settings.gold.to
      )
        return (
          <div className={b('td-data-icon-wrapper')}>
            <SVG svgProps={{ svg: iconSVG }} className={b('td-data-icon', { color: 'gold' })} />
            <div className={b('td-data-icon-text')}>{locale.gold}</div>
          </div>
        );

      if (
        item.totalDeposits >= deposit_level_settings.silver.from &&
        item.totalDeposits <= deposit_level_settings.silver.to
      )
        return (
          <div className={b('td-data-icon-wrapper')}>
            <SVG svgProps={{ svg: iconSVG }} className={b('td-data-icon', { color: 'silver' })} />
            <div className={b('td-data-icon-text')}>{locale.silver}</div>
          </div>
        );

      if (
        item.totalDeposits >= deposit_level_settings.bronze.from &&
        item.totalDeposits <= deposit_level_settings.bronze.to
      )
        return (
          <div className={b('td-data-icon-wrapper')}>
            <SVG svgProps={{ svg: iconSVG }} className={b('td-data-icon', { color: 'bronze' })} />
            <div className={b('td-data-icon-text')}>{locale.bronze}</div>
          </div>
        );
    }

    return (
      <div className={b('td-data-icon-wrapper')}>
        <SVG svgProps={{ svg: userTypes[item?.role]?.icon }} className={b('td-data-icon')} />
      </div>
    );
  }, [item, deposit_level_settings, locale]);

  return (
    <div className={b({ banned: item.isBanned })}>
      <div className={b('td')}>
        <div className={b('td-data')}>
          {icon}
          <div className={b('td-data-user')}>
            <span>{item.username}</span>
            <div className={b('td-data-user-type', { role: item.role })}>{userTypes[item.role].type}</div>
          </div>
        </div>
      </div>
      <div className={b('td')}>{item.balance}</div>
      <div className={b('td')}>
        <div className={b('td-buttons')}>
          {isDepositAccess && canOperate && (
            <Button disabled={item.isBanned} link={`/user/deposit/${item.id}`} text={locale.topup} />
          )}
          {isWithdrawalAccess && canOperate && (
            <Button
              disabled={item.isBanned}
              link={`/user/withdrawal/${item.id}`}
              color="full-transparent"
              text={locale.withdrawal}
            />
          )}
        </div>
      </div>
      <div className={b('td')}>{item.creationDate}</div>
      <div className={b('td')}>{item.commissionPercent && `${item.commissionPercent}%`}</div>
      <div className={b('td')}>
        {+item.role !== 3 && (
          <div className={b('td-actions')}>
            <Dropdown
              user={item}
              items={getActionItems({ role: item.role, isBanned: item.isBanned, item })}
              locale={locale}
            />
          </div>
        )}
      </div>
    </div>
  );
};

TableRow.propTypes = {
  item: PropTypes.object.isRequired,
};

export default TableRow;
